import React, { useEffect, useRef, useState } from 'react';
import classnames from 'classnames';

import styles from './RegionSelector.module.scss';
import ArrowDown from 'app/assets/Icons/ArrowDown.svg';
import { getCatiaRegions } from 'integration/claims/catastropheDetection';
import { displayToastError } from 'app';
import { LoadingSpinner } from 'app/components';

interface RegionData {
  code: string;
  displayName: string;
  countryCode: string;
  active: boolean;
}

interface RegionSelectorProps {
  handleOnRegionChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
}

export const RegionSelector: React.FC<RegionSelectorProps> = ({
  handleOnRegionChange
}) => {
  const [searchRegion, setSearchRegion] = useState<string>('');
  const [selectedRegion, setSelectedRegion] = useState<RegionData | null>(null);
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const [regions, setRegions] = useState<RegionData[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const sortRegionsByDisplayName = (arr: RegionData[]): RegionData[] =>
    [...arr].sort((a, b) => a.displayName.localeCompare(b.displayName));

  useEffect(() => {
    let isMounted = true;
    const loadRegions = async () => {
      setIsLoading(true);
      try {
        const catiaRegions = await getCatiaRegions(
          'claims',
          'catastrophe-detection'
        );
        if (isMounted) setRegions(sortRegionsByDisplayName(catiaRegions));
      } catch (error: any) {
        const message = error?.message || 'Error getting regions.';
        displayToastError(message, 'Region Selector');
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };
    loadRegions();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredRegions = sortRegionsByDisplayName(
    regions.filter(
      (region: RegionData) =>
        region.code.toLowerCase().includes(searchRegion.toLowerCase()) ||
        region.displayName.toLowerCase().includes(searchRegion.toLowerCase())
    )
  );

  return (
    <div ref={dropdownRef} className={styles['language-selector']}>
      <div className={styles['selector-container']}>
        <button
          type="button"
          disabled={isLoading}
          className={`${styles['custom-select']} ${
            isLoading ? styles.disabled : ''
          }`}
          onClick={() => !isLoading && setShowDropdown(!showDropdown)}
          aria-haspopup="listbox"
          aria-expanded={showDropdown}
        >
          <span>
            {selectedRegion
              ? `Selected region: ${selectedRegion.displayName}`
              : `Please select a region`}
          </span>
          <div className={styles['toggle-icon']}>
            <img
              src={ArrowDown}
              alt="Arrow Down"
              className={classnames({
                [styles.rotate]: showDropdown
              })}
            />
          </div>
        </button>
        {isLoading && (
          <div className={styles['loader-container']}>
            <LoadingSpinner
              containerStyle={{
                position: 'relative',
                width: '1.5rem',
                height: '1.5rem',
                backdropFilter: 'none',
                background: 'transparent'
              }}
              imageStyle={{
                width: '1.5rem',
                height: '1.5rem',
                marginLeft: '-1.5rem'
              }}
            />
          </div>
        )}
      </div>
      {showDropdown && !isLoading && (
        <div
          className={classnames(styles['dropdown-content'], {
            [styles['left-alignment']]: true
          })}
        >
          <div className={styles['search-language']}>
            <input
              type="text"
              placeholder="Search Region"
              value={searchRegion}
              onChange={(e) => setSearchRegion(e.target.value)}
              className={styles['search-bar']}
            />
          </div>
          <ul className={styles.options} aria-label="Region list">
            {filteredRegions.map((region) => (
              <li key={region.code}>
                <button
                  type="button"
                  className={styles.option}
                  onClick={() => {
                    setSelectedRegion(region);
                    handleOnRegionChange({
                      target: { value: region.code }
                    } as any);
                    setShowDropdown(false);
                  }}
                  aria-pressed={selectedRegion?.code === region.code}
                >
                  {region.displayName}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default RegionSelector;
